/**
 * InsightsCards — small, focused cards that mount on existing pages to
 * surface previously-hidden Postgres signals (Phase 1/2 additions):
 *
 *   <RefundsQueueCard />        — Audit page
 *   <PayoutQueueCard />         — Operations page
 *   <CreatorConcentrationCard/> — Investor page (range-aware)
 *   <CohortRetentionCard />     — Home page
 *   <UnitEconomicsBlock />      — Investor page (LTV, margin, referred-vs-organic)
 *
 * Every card uses the same visual primitives (label + big number + sub-line
 * + provenance footer) so they read as one consistent "insights" family.
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import {
  useRefundsQueue, usePayoutQueue, useCreatorConcentration,
  useCohortRetention, useUnitEconomics,
} from "./useInsights.v2";
import { SkelSection } from "./PageState.v2";

/** Card-shaped skeleton for an insight card while its fetch is in flight. */
function InsightSkeleton({ height = 160 }) {
  return <SkelSection height={height} />;
}

function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
function formatNum(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-IN");
}

function CardShell({ accent = "#FC8019", title, subtitle, children, footer }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderLeft: `4px solid ${accent}`,
      borderRadius: 14, padding: 16,
      boxShadow: "0 1px 2px rgba(31,42,63,0.04)",
      display: "flex", flexDirection: "column", gap: 10,
      fontFamily: T.font.sans,
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
      {footer && <div style={{ fontSize: 10, color: T.text.muted, fontStyle: "italic", marginTop: 4 }}>{footer}</div>}
    </div>
  );
}

function BucketRow({ label, count, accent, hot }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${T.border.subtle}` }}>
      <span style={{ fontSize: 12, color: hot ? "#A82A2A" : T.text.secondary, fontWeight: hot ? 700 : 500 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: hot ? "#A82A2A" : T.text.primary, fontVariantNumeric: "tabular-nums" }}>{formatNum(count)}</span>
    </div>
  );
}

// ─── Refunds queue (Audit) ──────────────────────────────────
export function RefundsQueueCard() {
  const { data, loading } = useRefundsQueue();
  if (loading) return <InsightSkeleton />;
  if (!data?.summary) return null;
  const s = data.summary;
  const hot = Number(s.age_30_plus) > 0 || Number(s.oldest_age_days) > 30;
  return (
    <CardShell
      accent={hot ? "#FF4757" : "#FC8019"}
      title="Refunds queue — approved, awaiting completion"
      subtitle={`${formatNum(s.total_count)} pending · ${formatINR(s.total_amount)} owed · oldest ${Number(s.oldest_age_days || 0).toFixed(1)} days`}
      footer="Source: refund_requests · refund_status='approved' · age = NOW() − created_at"
    >
      <div>
        <BucketRow label="0–7 days"   count={s.age_0_7} />
        <BucketRow label="8–14 days"  count={s.age_8_14} />
        <BucketRow label="15–30 days" count={s.age_15_30} hot={Number(s.age_15_30) > 0} />
        <BucketRow label="30+ days (SLA breach)" count={s.age_30_plus} hot={Number(s.age_30_plus) > 0} />
      </div>
    </CardShell>
  );
}

// ─── Payout queue (Operations) ──────────────────────────────
export function PayoutQueueCard() {
  const { data, loading } = usePayoutQueue();
  if (loading) return <InsightSkeleton />;
  if (!data?.summary) return null;
  const s = data.summary;
  const hot = Number(s.age_96h_plus) > 0 || Number(s.oldest_age_hours) > 96;
  return (
    <CardShell
      accent={hot ? "#FF4757" : "#7C4DFF"}
      title="Pending payouts — creator + brand withdrawals"
      subtitle={`${formatNum(s.total_count)} pending · ${formatINR(s.total_amount)} owed · ${formatNum(s.creator_count)} creator + ${formatNum(s.brand_count)} brand · oldest ${Number(s.oldest_age_hours || 0).toFixed(1)}h`}
      footer="Source: withdraw_requests + brand_withdraw_requests · status IN ('pending','inprogress')"
    >
      <div>
        <BucketRow label="0–24h"       count={s.age_0_24h} />
        <BucketRow label="24–48h"      count={s.age_24_48h} />
        <BucketRow label="48–96h (SLA breach)"  count={s.age_48_96h} hot={Number(s.age_48_96h) > 0} />
        <BucketRow label="96h+ (urgent)" count={s.age_96h_plus} hot={Number(s.age_96h_plus) > 0} />
      </div>
    </CardShell>
  );
}

// ─── Creator concentration (Investor) ───────────────────────
export function CreatorConcentrationCard({ range, rangeId }) {
  const { data, loading } = useCreatorConcentration(range, rangeId);
  if (loading) return <InsightSkeleton />;
  if (!data?.summary) return null;
  const s = data.summary;
  const top10Pct = Number(s.top10_pct) || 0;
  const danger = top10Pct >= 70;
  const accent = top10Pct >= 70 ? "#FF4757" : top10Pct >= 50 ? "#FFC107" : "#0E7B47";
  return (
    <CardShell
      accent={accent}
      title="Creator concentration — earnings dependency"
      subtitle={`${formatNum(s.creator_count)} earning creators in window · ${formatINR(s.total_earnings)} total payouts`}
      footer="Source: earnings ranked by celebrity_id — % of total going to top N"
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        <ConcentrationBar label="Top 5"  pct={Number(s.top5_pct) || 0} accent={accent} />
        <ConcentrationBar label="Top 10" pct={top10Pct} accent={accent} highlight />
        <ConcentrationBar label="Top 20" pct={Number(s.top20_pct) || 0} accent={accent} />
      </div>
      {danger && (
        <div style={{ fontSize: 11, color: "#A82A2A", fontWeight: 700, marginTop: 4 }}>
          ⚠ Top-10 share &gt; 70% — high single-creator dependency risk.
        </div>
      )}
    </CardShell>
  );
}

function ConcentrationBar({ label, pct, accent, highlight }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: highlight ? 22 : 18, fontWeight: 800, color: accent, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
        {pct.toFixed(1)}%
      </div>
      <div style={{ height: 4, background: T.surface.canvas, borderRadius: 999 }}>
        <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: accent, borderRadius: 999 }} />
      </div>
    </div>
  );
}

// ─── Cohort retention (Home) ────────────────────────────────
export function CohortRetentionCard() {
  const { data, loading } = useCohortRetention();
  if (loading) return <InsightSkeleton />;
  if (!data?.cohorts?.length) return null;
  const cohorts = data.cohorts.slice(-6); // most recent 6 cohorts
  const monthLabel = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
  };
  return (
    <CardShell
      accent="#0E7B47"
      title="Cohort retention — fan signups by month"
      subtitle="What % of fans who signed up in each month made a paid call within 30 / 60 / 90 days"
      footer="Source: users (role_id=2, signup month) joined with first paid transaction_history"
    >
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font.sans, fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border.subtle}`, fontSize: 10, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 700 }}>Cohort</th>
              <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>Signups</th>
              <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>30-day</th>
              <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>60-day</th>
              <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 700 }}>90-day</th>
            </tr>
          </thead>
          <tbody>
            {cohorts.map((c, i) => (
              <tr key={i} style={{ borderBottom: i < cohorts.length - 1 ? `1px solid ${T.border.subtle}` : "none", fontSize: 12 }}>
                <td style={{ padding: "8px", fontWeight: 700, color: T.text.primary }}>{monthLabel(c.cohortMonth)}</td>
                <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{formatNum(c.cohortSize)}</td>
                <td style={{ padding: "8px", textAlign: "right", fontWeight: 700, color: T.text.primary }}>{c.pct30}%</td>
                <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{c.pct60}%</td>
                <td style={{ padding: "8px", textAlign: "right", color: T.text.secondary }}>{c.pct90}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardShell>
  );
}

// ─── Unit economics block (Investor) ─────────────────────────
export function UnitEconomicsBlock() {
  const { data, loading } = useUnitEconomics();
  if (loading) return <InsightSkeleton />;
  if (!data?.ltv) return null;
  const { ltv, margin, referredVsOrganic = [] } = data;
  const rvo = (s) => referredVsOrganic.find(r => r.source === s) || {};
  const ref = rvo("referred");
  const org = rvo("organic");

  return (
    <div style={{ display: "grid", gap: 12, marginBottom: 14 }}>
      <CardShell
        accent="#7C4DFF"
        title="Unit economics — fan LTV"
        subtitle={`${formatNum(ltv.payingFans)} paying fans · lifetime spend distribution`}
        footer="Source: lifetime SUM(transaction_history.total_amount) per fan · paid + non-refund + non-topup"
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))", gap: 10 }}>
          <Mini label="Avg LTV"     value={formatINR(ltv.avgLtv)} />
          <Mini label="Median LTV"  value={formatINR(ltv.medianLtv)} />
          <Mini label="Top-10% LTV" value={formatINR(ltv.p90Ltv)} />
          <Mini label="Avg calls / fan" value={formatNum(ltv.avgCalls)} />
        </div>
      </CardShell>

      <CardShell
        accent="#0E7B47"
        title="Gross margin (90-day proxy)"
        subtitle={`${formatINR(margin.gmv90d)} gross · ${formatINR(margin.payouts90d)} creator payouts · ${formatINR(margin.refunds90d)} refunds`}
        footer="Source: (gmv − creator earnings − approved refunds) / gmv. Excludes infra + payroll."
      >
        <div style={{ fontSize: 32, fontWeight: 800, color: margin.marginPct == null ? T.text.muted : (margin.marginPct >= 0 ? "#0E7B47" : "#A82A2A"), fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
          {margin.marginPct == null ? "—" : `${margin.marginPct.toFixed(1)}%`}
        </div>
      </CardShell>

      <CardShell
        accent="#FF6B9D"
        title="Referred vs organic — LTV comparison"
        subtitle="Same lifetime-spend formula, split by whether the fan came in via the referral system"
        footer="Source: user_referral.referred_user (filter role_id=-7 fake account) joined with paying fans"
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <SourceCard tag="Referred" data={ref} accent="#0E7B47" />
          <SourceCard tag="Organic"  data={org} accent="#7C4DFF" />
        </div>
        {ref.avgLtv > 0 && org.avgLtv > 0 && (
          <div style={{ fontSize: 11, color: T.text.secondary, marginTop: 2 }}>
            Referred LTV is{" "}
            <strong style={{ color: ref.avgLtv > org.avgLtv ? "#0E7B47" : "#A82A2A" }}>
              {((ref.avgLtv / org.avgLtv - 1) * 100).toFixed(0)}%
            </strong>{" "}
            {ref.avgLtv > org.avgLtv ? "higher" : "lower"} than organic.
          </div>
        )}
      </CardShell>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: T.text.primary, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>{value}</div>
    </div>
  );
}

function SourceCard({ tag, data, accent }) {
  return (
    <div style={{ background: T.surface.canvas, border: `1px solid ${T.border.subtle}`, borderLeft: `3px solid ${accent}`, borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{tag}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: T.text.primary, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>{formatINR(data.avgLtv)}</div>
      <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>
        avg LTV · {formatNum(data.payingFans)} fans · {formatNum(data.avgCalls)} calls/fan
      </div>
    </div>
  );
}
